import React, { useEffect, useState } from "react";
import { User } from "lucide-react";
import { toast } from "sonner";
import { useSubscriberAuth } from "@/contexts/SubscriberAuthContext";
import subscriberApi from "@/lib/subscriberApi";
import ProfilePhotoField from "@/components/ProfilePhotoField";
import FaceSamplesField from "@/components/FaceSamplesField";
import ResponsivaDocumentField from "@/components/ResponsivaDocumentField";
import { subscriberPhotoSrc } from "@/components/SubscriberAvatar";
import { descriptorsFromDataUrls, loadFaceModels } from "@/lib/faceRecognition";
import { fmtDate } from "@/lib/utils-app";

export default function SubscriberProfile() {
  const { subscriber, refreshMe } = useSubscriberAuth();
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [responsiblePhoto, setResponsiblePhoto] = useState(null);
  const [faceSamples, setFaceSamples] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!subscriber) return;
    setProfilePhoto(subscriber.profile_photo || subscriber.profile_photo_thumb || null);
    setResponsiblePhoto(subscriber.responsible_photo || subscriber.responsible_photo_thumb || null);
    setFaceSamples([]);
    loadFaceModels().catch(() => {});
  }, [subscriber?.id]);

  const savePhotos = async () => {
    if (!subscriber?.id) return;
    setSaving(true);
    try {
      const payload = {};
      const initialProfile = subscriber.profile_photo || null;
      const initialResponsible = subscriber.responsible_photo || null;
      const profileChanged = profilePhoto !== (initialProfile || subscriber.profile_photo_thumb);
      if (profileChanged) {
        payload.profile_photo = profilePhoto || null;
      }
      if (responsiblePhoto !== (initialResponsible || subscriber.responsible_photo_thumb)) {
        payload.responsible_photo = responsiblePhoto || null;
      }
      const hasNewSamples = faceSamples.length > 0;
      if (!Object.keys(payload).length && !hasNewSamples) {
        toast.info("Sin cambios en las fotos");
        return;
      }
      if (profileChanged || hasNewSamples) {
        const sources = [profilePhoto, ...faceSamples].filter(Boolean);
        if (sources.length) {
          try {
            const descriptors = await descriptorsFromDataUrls(sources);
            if (descriptors.length) {
              payload.face_descriptors = descriptors;
              payload.face_descriptor = descriptors[0];
            }
          } catch {
            toast.warning("No se pudo actualizar el rostro para asistencia facial.");
          }
        }
      }
      await subscriberApi.put("/subscriber/me", payload);
      await refreshMe();
      toast.success("Fotos actualizadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron guardar las fotos");
    } finally {
      setSaving(false);
    }
  };

  const displayPhoto = subscriberPhotoSrc(subscriber, { preferThumb: false }) || profilePhoto;

  return (
    <section className="space-y-4" data-testid="subscriber-profile-page">
      <h1 className="font-display text-4xl text-white">Mi perfil</h1>
      <div className="glass p-6 rounded-sm flex flex-wrap items-center gap-6">
        <div className="h-28 w-28 rounded-full overflow-hidden bg-white/5 grid place-items-center shrink-0">
          {displayPhoto ? (
            <img src={displayPhoto} alt="" className="h-full w-full object-cover" />
          ) : (
            <User className="text-white/40" size={40} />
          )}
        </div>
        <div>
          <div className="font-display text-3xl text-white">{subscriber?.full_name || "—"}</div>
          <div className="text-sm text-white/60 mt-1">{subscriber?.email || "—"}</div>
          {subscriber?.package?.name && (
            <span className="inline-block mt-2 badge-status badge-valid">{subscriber.package.name}</span>
          )}
        </div>
      </div>

      <div className="glass p-6 rounded-sm space-y-6 max-w-xl">
        <h2 className="font-display text-2xl text-white">Foto y carta responsiva</h2>
        <ProfilePhotoField
          value={profilePhoto}
          onChange={setProfilePhoto}
          label="Foto del atleta"
          hint="Se usa en asistencia facial del gimnasio."
          cameraTitle="Tomar foto del atleta"
        />
        <FaceSamplesField
          value={faceSamples}
          onChange={setFaceSamples}
          existingCount={
            Array.isArray(subscriber?.face_descriptors)
              ? subscriber.face_descriptors.length
              : (subscriber?.face_descriptor ? 1 : 0)
          }
        />
        <ResponsivaDocumentField
          value={responsiblePhoto}
          onChange={setResponsiblePhoto}
        />
        <button type="button" onClick={savePhotos} disabled={saving} className="btn-primary text-sm py-2">
          {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {[
          ["Usuario", subscriber?.username],
          ["Email", subscriber?.email],
          ["Teléfono", subscriber?.phone],
          ["Paquete actual", subscriber?.package?.name || "—"],
          ["Beneficios", (subscriber?.package?.benefits || []).join(", ") || "—"],
          ["Vencimiento", fmtDate(subscriber?.next_due_date)],
          ["Cambio pendiente", subscriber?.pending_package?.name || "Sin cambio programado"],
        ].map(([k, v]) => (
          <div key={k} className="glass p-4 rounded-sm">
            <div className="text-xs uppercase tracking-widest text-white/40">{k}</div>
            <div className="mt-1 text-white">{v || "—"}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
